import { Check, AlertCircle, Leaf, ExternalLink } from 'lucide-react';

// Model *tags* (the short "which models" line) come from live data
// (provider.models/defaultModel — GET /api/settings/providers, sourced from
// PROVIDERS in factory.js), not hardcoded here, so this table never drifts
// out of sync with the real model lists again. `tag` below survives only for
// Ollama, whose models are installed locally and never appear in this list.
const PROVIDER_META = {
  gemini: {
    glyph: '✦',
    tone: 'violet',
    desc: 'Multimodale Modelle von Google — stark bei Bild-, Audio- und Video-Eingaben.',
    pros: ['Multimodal · Bild + Video', 'Großes Kontextfenster · 2M Token', 'Günstig in Skalierung'],
    docs: 'aistudio.google.com/apikey',
  },
  openai: {
    glyph: '◎',
    tone: 'moss',
    desc: 'GPT-Modelle. Ausgewogen für Reasoning, Code und Tool-Use.',
    pros: ['Tool-Calling reif', 'Stabile Code-Qualität', 'JSON-Mode + Vision'],
    docs: 'platform.openai.com/api-keys',
  },
  anthropic: {
    glyph: '❖',
    tone: 'indigo',
    desc: 'Claude — empfohlen für lange Dokumente, sorgfältiges Reasoning und sicheres Verhalten.',
    pros: ['200k Token Kontext', 'Stark bei Lerninhalten', 'Sicheres Verhalten · Constitution'],
    docs: 'console.anthropic.com/settings/keys',
    recommended: true,
  },
  mistral: {
    glyph: '≋',
    tone: 'moss',
    desc: 'Mistral AI — Sitz und Infrastruktur in der EU, feste EU-Datenverarbeitung.',
    pros: ['EU-Datenverarbeitung fix', 'Schnell und kosteneffizient', 'Passend für „Nur EU-Verarbeitung"'],
    docs: 'console.mistral.ai/api-keys',
  },
  ollama: {
    tag: 'llama3 · mistral · qwen',
    glyph: '⌂',
    tone: 'amber',
    desc: 'Modelle lokal auf deinem Rechner — keine Cloud, volle Datenkontrolle.',
    pros: ['100 % lokal · keine Cloud', 'Kostenlos nach Setup', 'Funktioniert offline'],
    docs: 'ollama.com/download',
  },
};

const STATUS_META = {
  ready:   { cls: 'is-ready',   label: 'Bereit',           Icon: Check },
  missing: { cls: 'is-missing', label: 'Nicht eingerichtet', Icon: AlertCircle },
  local:   { cls: 'is-local',   label: 'Lokal',            Icon: Leaf },
};

/**
 * The short "which models" line under the provider name. Prefers the live
 * model list (provider.models, newest-first per factory.js) so a new vendor
 * release never needs a manual edit here — falls back to the static `tag`
 * (Ollama only: its models are installed locally, never in this list).
 */
function providerTag(provider, meta) {
  if (provider.models?.length) {
    return provider.models.slice(0, 2).map((m) => m.name).join(' / ');
  }
  return meta.tag || provider.id;
}

export default function ProviderSelector({ providers, selectedProvider, onSelect }) {
  return (
    <div className="provider-grid" role="radiogroup" aria-label="KI-Anbieter wählen">
      {providers.map((provider) => {
        const meta = PROVIDER_META[provider.id] || {};
        const status = provider.id === 'ollama'
          ? 'local'
          : provider.configured
            ? 'ready'
            : 'missing';
        const s = STATUS_META[status];
        const StatusIcon = s.Icon;
        const isSelected = provider.id === selectedProvider;

        return (
          <button
            key={provider.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={!provider.configured && provider.id !== 'ollama'}
            onClick={() => onSelect?.(provider.id)}
            className={`provider-card prov-${meta.tone || 'indigo'} ${isSelected ? 'is-selected' : ''} ${!provider.configured && provider.id !== 'ollama' ? 'is-disabled' : ''}`}
          >
            <header className="provider-card-head">
              <div className="provider-glyph" aria-hidden="true">{meta.glyph || '·'}</div>
              <div className="provider-card-titles">
                <div className="provider-name">
                  <span>{provider.name}</span>
                  {meta.recommended && <span className="badge badge-ready">Empfohlen</span>}
                  {provider.dataRegion === 'eu' && (
                    <span className="badge badge-ready" title="Verarbeitung in der EU (fest)">EU</span>
                  )}
                </div>
                <span className="provider-tag">{providerTag(provider, meta)}</span>
              </div>
              <span className={`prov-status ${s.cls}`}>
                <StatusIcon size={12} />
                <span>{s.label}</span>
              </span>
            </header>
            {meta.desc && <p className="provider-desc">{meta.desc}</p>}
            {meta.pros?.length > 0 && (
              <ul className="provider-pros">
                {meta.pros.map((pro) => (
                  <li key={pro}>
                    <Check size={18} />
                    <span>{pro}</span>
                  </li>
                ))}
              </ul>
            )}
            <footer className="provider-foot">
              {meta.docs && (
                <a
                  className="provider-docs"
                  href={`https://${meta.docs}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink size={12} />
                  <span>{meta.docs}</span>
                </a>
              )}
              <span className="provider-mark" aria-hidden="true">
                <Check size={14} />
              </span>
            </footer>
          </button>
        );
      })}
    </div>
  );
}
